import React, { useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { CtaButton, Eyebrow, KineticTitle, PageMark, SceneStage } from "./GhxpUi";
import { MEETING_CLIPS } from "./ghxpAssets";

// Cena 2 — "Uma experiência para aproximar a GH de seus clientes." (pág. 2), no mesmo padrão do hero e do local:
// vídeo do evento real da Experience House em tela cheia, um clipe por tema (relacionamento, conversa, gastronomia).
// Os temas avançam sozinhos (a barra laranja marca o tempo); passar o mouse, focar ou tocar escolhe o tema
// e pausa o avanço. Só o clipe do tema atual toca; os outros esperam parados por baixo.

function Clip({ clip, portrait, current, playing, load }) {
  const ref = useRef(null);

  // Começa do início ao virar o tema atual; o anterior pausa depois do fade.
  useEffect(() => {
    const video = ref.current;
    if (!video) return undefined;
    if (current && playing) {
      video.play().catch(() => {});
      return undefined;
    }
    if (!current) {
      const timer = window.setTimeout(() => {
        video.pause();
        video.currentTime = 0;
      }, 900);
      return () => window.clearTimeout(timer);
    }
    video.pause();
    return undefined;
  }, [current, playing, portrait]);

  // O Safari ignora a troca de preload="none" para "auto": dispara o download se nada começou.
  useEffect(() => {
    const video = ref.current;
    if (!load || !video) return undefined;
    const timer = window.setTimeout(() => {
      if (video.networkState !== HTMLMediaElement.NETWORK_LOADING && video.readyState === HTMLMediaElement.HAVE_NOTHING) {
        video.load();
      }
    }, 250);
    return () => window.clearTimeout(timer);
  }, [load, portrait]);

  return (
    <video
      ref={ref}
      key={portrait ? "tall" : "wide"}
      className={`ghxpMeeting__clip ${current ? "is-current" : ""}`}
      src={portrait ? clip.tall : clip.wide}
      poster={load ? (portrait ? clip.posterTall : clip.poster) : undefined}
      muted
      loop
      playsInline
      preload={load ? "auto" : "none"}
    />
  );
}

function GhxpMeeting({ onStart, active, settled, warm, lowPower, portrait }) {
  const { t } = useTranslation();
  const [theme, setTheme] = useState(0);
  const [hidden, setHidden] = useState(() => document.hidden);

  useEffect(() => {
    const onVisibility = () => setHidden(document.hidden);
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  const playing = active && settled && !hidden;
  const advance = (event) => {
    if (event.animationName === "ghxpThemeBar") setTheme((value) => (value + 1) % MEETING_CLIPS.length);
  };

  return (
    <>
      <SceneStage shade="meeting" className="ghxpMeeting__stage">
        {MEETING_CLIPS.map((clip, i) =>
          lowPower ? (
            warm && (
              <img
                key={clip.key}
                className={`ghxpMeeting__clip ${i === theme ? "is-current" : ""}`}
                src={portrait ? clip.posterTall : clip.poster}
                alt=""
                decoding="async"
              />
            )
          ) : (
            <Clip
              key={clip.key}
              clip={clip}
              portrait={portrait}
              current={i === theme}
              playing={playing}
              load={warm && (i === theme || active)}
            />
          )
        )}
      </SceneStage>

      <div className="ghxpMeeting__content ghxpDepth" data-scrollable="y">
        <Eyebrow className="ghxpReveal">{t("ghxp_meeting_eyebrow")}</Eyebrow>
        <KineticTitle text={t("ghxp_meeting_title")} className="ghxpDisplay ghxpMeeting__title" />

        <ol className="ghxpThemes ghxpReveal" aria-label={t("ghxp_meeting_eyebrow")}>
          {MEETING_CLIPS.map((clip, i) => (
            <li key={clip.key}>
              <button
                type="button"
                className={`ghxpTheme ${i === theme ? "is-active" : ""}`}
                aria-pressed={i === theme}
                tabIndex={active ? 0 : -1}
                onMouseEnter={() => setTheme(i)}
                onFocus={() => setTheme(i)}
                onClick={() => setTheme(i)}
              >
                <span className="ghxpTheme__index">{String(i + 1).padStart(2, "0")}</span>
                <span className="ghxpTheme__body">
                  <span className="ghxpTheme__word">{t(`ghxp_meeting_word_${i + 1}`)}</span>
                  <span className="ghxpTheme__caption">{t(`ghxp_meeting_caption_${i + 1}`)}</span>
                  <span className="ghxpTheme__bar" aria-hidden="true">
                    {i === theme && <i key={theme} onAnimationEnd={advance} />}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ol>

        <div className="ghxpMeeting__footer ghxpReveal">
          <p className="ghxpMeeting__text">
            <Trans i18nKey="ghxp_meeting_text" />
          </p>
          <CtaButton onClick={onStart}>{t("ghxp_cta")}</CtaButton>
        </div>
      </div>

      <PageMark />
    </>
  );
}

export default GhxpMeeting;
